import React from 'react';
import { peopleProfiles, linkedInPosts } from '../utils/mockData';
import { MessageSquareIcon, ThumbsUpIcon, ShareIcon, RefreshCwIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
interface EngagementContextProps {
  personId: number;
  onGenerateMessage: (context?: string) => void;
}
export const EngagementContext: React.FC<EngagementContextProps> = ({
  personId,
  onGenerateMessage
}) => {
  const person = peopleProfiles.find(p => p.id === personId);
  if (!person) return null;
  const getPostById = (postId: number) => {
    return linkedInPosts.find(post => post.id === postId);
  };
  const handleGenerateFromContext = (context: string) => {
    onGenerateMessage(context);
  };
  // Extract topics of interest based on interactions
  const getTopicsOfInterest = () => {
    const topics = new Set<string>();
    person.interactions.forEach(interaction => {
      const post = getPostById(interaction.postId);
      if (post) {
        // Extract potential topics from post content
        const content = post.content.toLowerCase();
        if (content.includes('sales')) topics.add('sales');
        if (content.includes('marketing')) topics.add('marketing');
        if (content.includes('lead')) topics.add('lead generation');
        if (content.includes('strategy')) topics.add('strategy');
        if (content.includes('growth')) topics.add('growth');
        if (content.includes('technology')) topics.add('technology');
        if (content.includes('ai') || content.includes('artificial intelligence')) topics.add('AI');
        if (content.includes('data')) topics.add('data');
      }
      // Extract from comments
      if (interaction.comment) {
        const comment = interaction.comment.toLowerCase();
        if (comment.includes('sales')) topics.add('sales');
        if (comment.includes('marketing')) topics.add('marketing');
        if (comment.includes('lead')) topics.add('lead generation');
        if (comment.includes('strategy')) topics.add('strategy');
        if (comment.includes('growth')) topics.add('growth');
        if (comment.includes('technology')) topics.add('technology');
        if (comment.includes('ai') || comment.includes('artificial intelligence')) topics.add('AI');
        if (comment.includes('data')) topics.add('data');
      }
    });
    return Array.from(topics);
  };
  const topicsOfInterest = getTopicsOfInterest();
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-284">
      <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-285">
        <ZapIcon size={16} className="text-[#f9b30f] mr-2" data-id="element-286" />
        Engagement Context
      </h3>
      {topicsOfInterest.length > 0 && <div className="mb-4" data-id="element-287">
          <div className="text-sm text-gray-600 mb-2" data-id="element-288">Topics of interest:</div>
          <div className="flex flex-wrap gap-2" data-id="element-289">
            {topicsOfInterest.map((topic, index) => <span key={index} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium" data-id="element-290">
                {topic}
              </span>)}
          </div>
        </div>}
      <div className="space-y-3" data-id="element-291">
        {person.interactions.map((interaction, index) => {
        const post = getPostById(interaction.postId);
        if (!post) return null;
        return <div key={index} className="p-3 bg-gray-50 rounded-md border border-gray-200" data-id="element-292">
              <div className="flex items-start" data-id="element-293">
                <div className="flex-shrink-0 mt-1" data-id="element-294">
                  {interaction.type === 'liked' ? <ThumbsUpIcon size={14} className="text-blue-600" data-id="element-295" /> : interaction.type === 'commented' ? <MessageSquareIcon size={14} className="text-purple-600" data-id="element-296" /> : <ShareIcon size={14} className="text-green-600" data-id="element-297" />}
                </div>
                <div className="ml-2 flex-1" data-id="element-298">
                  <p className="text-xs text-gray-500" data-id="element-299">
                    {interaction.type === 'liked' ? 'Liked a post' : interaction.type === 'commented' ? 'Commented on a post' : 'Shared a post'}{' '}
                    {interaction.date}
                  </p>
                  <p className="text-sm text-gray-800 line-clamp-2 mt-1" data-id="element-300">
                    "{post.content.substring(0, 100)}..."
                  </p>
                  {interaction.comment && <div className="mt-2 text-xs bg-white p-2 rounded border border-gray-200" data-id="element-301">
                      <span className="text-gray-500 block mb-1" data-id="element-302">
                        Their comment:
                      </span>
                      <span className="italic" data-id="element-303">"{interaction.comment}"</span>
                    </div>}
                  <button onClick={() => handleGenerateFromContext(`Based on their ${interaction.type === 'commented' ? 'comment' : 'engagement'} on a post about: "${post.content.substring(0, 100)}..."`)} className="mt-2 inline-flex items-center text-xs font-medium text-[#f9b30f] hover:text-[#e0a40e]" data-id="element-304">
                    Generate message from this context
                    <ArrowRightIcon size={12} className="ml-1" data-id="element-305" />
                  </button>
                </div>
              </div>
            </div>;
      })}
      </div>
      {person.interactions.length === 0 && <div className="text-center p-4" data-id="element-306">
          <p className="text-sm text-gray-500" data-id="element-307">No engagement data available.</p>
        </div>}
      <button onClick={() => onGenerateMessage(`Based on all engagement data from ${person.name}`)} className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-[#f9b30f] text-sm font-medium rounded-md text-[#f9b30f] bg-white hover:bg-[#fff8e8] focus:outline-none" data-id="element-308">
        <RefreshCwIcon size={16} className="mr-2" data-id="element-309" />
        Generate from all engagement
      </button>
    </div>;
};